import { useEffect, useState } from 'react';

// graphql
import { useTerminalGainLossQuery, useCpGetChipsSummaryQuery, useTotalCashierCashQuery } from '@/generated/graphql';

// material ui
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { GiCash } from 'react-icons/gi';
import { IoIosCash } from 'react-icons/io';
import { TbBrandSpeedtest } from 'react-icons/tb';
import { BsGraphUpArrow } from 'react-icons/bs';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import ImportExportIcon from '@mui/icons-material/ImportExport';

// interfaces
import { mapGQLChipsSummary } from '@/shared/interfaces';

interface ICashChipsSummary {
  cashiersChips: string;
  terminalLimit: string;
}

interface IProps {
  openManageCashMdl: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenManageCashiersChipsMdl: React.Dispatch<React.SetStateAction<boolean>>;
  chipsSummaryRefetch: string;
  setChipsSummaryRefetch: React.Dispatch<React.SetStateAction<string>>;
  setOpenManageTerminalChipsMdl: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenManageTerminalProfitLossMdl: React.Dispatch<React.SetStateAction<boolean>>;
  setTabValue: React.Dispatch<React.SetStateAction<string>>;
}

// helpers
import { formatNumber } from '@/shared/constants';

export const CashChipsSummary = ({
  openManageCashMdl,
  setOpenManageCashiersChipsMdl,
  chipsSummaryRefetch,
  setChipsSummaryRefetch,
  setOpenManageTerminalChipsMdl,
  setOpenManageTerminalProfitLossMdl,
  setTabValue,
}: IProps) => {
  const [cashChipsSummary, setCashChipsSummary] = useState<ICashChipsSummary>({
    cashiersChips: '0',
    terminalLimit: '0',
  });
  const [cashiersCash, setCashiersCash] = useState('0');
  const [terminalProfitLoss, setTerminalProfitLoss] = useState('0');

  // queries
  const terminalGainLossQuery = useTerminalGainLossQuery({
    onCompleted(data) {
      setTimeout(() => {
        setTerminalProfitLoss(formatNumber(data.terminalGainLoss));
      }, 100);
    },
  });

  const totalCashierCashQuery = useTotalCashierCashQuery({
    onCompleted(data) {
      setTimeout(() => {
        setCashiersCash(formatNumber(data.totalCashierCash));
      }, 100);
    },
  });

  const cpGetChipsSummaryQuery = useCpGetChipsSummaryQuery({
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const mappedData = mapGQLChipsSummary(data.chipsSummary);

      setTimeout(() => {
        setCashChipsSummary((prevSummary) => ({
          // Use functional update
          ...prevSummary,
          cashiersChips: formatNumber(mappedData.cashiersChips),
          terminalLimit: formatNumber(mappedData.terminalLimit),
        }));
      }, 100);
    },
  });

  useEffect(() => {
    if (chipsSummaryRefetch === 'cashier chips') {
      setChipsSummaryRefetch('');
      cpGetChipsSummaryQuery.refetch();
    }
  }, [chipsSummaryRefetch]);

  const clickManageBtn = (whichBtn: string) => {
    if (whichBtn === 'cashiers cash') {
      openManageCashMdl(true);
    } else if (whichBtn === 'cashiers chips') {
      setOpenManageCashiersChipsMdl(true);
      setTabValue('3');
    } else if (whichBtn === 'terminal balance') {
      setOpenManageTerminalChipsMdl(true);
      setTabValue('4');
    } else if (whichBtn === 'terminal profit loss') {
      setOpenManageTerminalProfitLossMdl(true);
      setTabValue('5');
    }
  };

  return (
    <>
      <Box sx={{ background: '#dfecf1' }} p={3}>
        <Grid container spacing={2}>
          <Grid size={8}>
            <Grid container spacing={2}>
              <Grid size={3}>
                <Card sx={{ background: '#d0e1e9' }}>
                  <CardContent>
                    <IoIosCash fontSize={25} />

                    <Typography variant="h5" fontWeight={900} gutterBottom>
                      ₱{cashiersCash}
                    </Typography>
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} display={'flex'}>
                      <Stack direction={'column'}>
                        <Typography variant="caption">Cashiers Cash</Typography>
                        <Typography variant="caption">Total Amount</Typography>
                      </Stack>
                      <Box
                        onClick={() => clickManageBtn('cashiers cash')}
                        sx={{ background: '#4295e7', borderRadius: 1, cursor: 'pointer' }}
                        p={1}>
                        <Stack direction={'column'} justifyContent={'center'} display={'flex'} justifyItems={'center'}>
                          <ImportExportIcon sx={{ fontSize: 23, mx: 'auto' }} />
                          <Typography fontSize={'10px'}>Transact</Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={3}>
                <Card sx={{ background: '#d0e1e9' }}>
                  <CardContent>
                    <GiCash fontSize={25} />
                    <Typography variant="h5" fontWeight={900} gutterBottom>
                      ₱{cashChipsSummary.cashiersChips}
                    </Typography>
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} display={'flex'}>
                      <Stack direction={'column'}>
                        <Typography variant="caption">Cashiers Chips</Typography>
                        <Typography variant="caption">Total Amount</Typography>
                      </Stack>
                      <Box
                        onClick={() => clickManageBtn('cashiers chips')}
                        sx={{ background: '#4295e7', borderRadius: 1, cursor: 'pointer' }}
                        p={1}>
                        <Stack direction={'column'} justifyContent={'center'} display={'flex'} justifyItems={'center'}>
                          <ImportExportIcon sx={{ fontSize: 23, mx: 'auto' }} />
                          <Typography fontSize={'10px'}>Transact</Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={3}>
                <Card sx={{ background: '#d0e1e9' }}>
                  <CardContent>
                    <TbBrandSpeedtest fontSize={25} />
                    <Typography variant="h5" fontWeight={900} gutterBottom>
                      {cashChipsSummary.terminalLimit}
                    </Typography>
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} display={'flex'}>
                      <Stack direction={'column'}>
                        <Typography variant="caption">Terminal Limit</Typography>
                        <Typography variant="caption">Amount</Typography>
                      </Stack>
                      <Box
                        onClick={() => clickManageBtn('terminal balance')}
                        sx={{ background: '#4295e7', borderRadius: 1, cursor: 'pointer' }}
                        p={1}>
                        <Stack direction={'column'} justifyContent={'center'} display={'flex'} justifyItems={'center'}>
                          <SettingsApplicationsIcon sx={{ fontSize: 23, mx: 'auto' }} />
                          <Typography fontSize={'10px'}>Manage</Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={3}>
                <Card sx={{ background: '#d0e1e9' }}>
                  <CardContent>
                    <BsGraphUpArrow fontSize={25} />
                    <Typography variant="h5" fontWeight={900} color="green" gutterBottom>
                      {terminalProfitLoss}
                    </Typography>
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} display={'flex'}>
                      <Stack direction={'column'}>
                        <Typography variant="caption">Terminal Profit Loss</Typography>
                        <Typography variant="caption">Amount</Typography>
                      </Stack>
                      <Box
                        onClick={() => clickManageBtn('terminal profit loss')}
                        sx={{ background: '#4295e7', borderRadius: 1, cursor: 'pointer' }}
                        p={1}>
                        <Stack direction={'column'} justifyContent={'center'} display={'flex'} justifyItems={'center'}>
                          <SettingsApplicationsIcon sx={{ fontSize: 23, mx: 'auto' }} />
                          <Typography fontSize={'10px'}>Manage</Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={4}></Grid>
        </Grid>
      </Box>
    </>
  );
};
